/**
 * OCR Anchor Pattern Utilities
 * 
 * Generates regex patterns from anchor text that tolerate common OCR misreads.
 * Used by the Match Settings panel in the Template Editor to help users
 * create robust anchor matching rules without manually writing regex.
 * 
 * CAPTURE-UI OWNED MODULE
 */

/**
 * Common OCR character confusions: maps a character to the set of characters
 * OCR engines commonly misread it as. Bidirectional — if 'l' can be misread
 * as '1' or 'I', then '1' and 'I' can also be misread as 'l'.
 */
const OCR_CONFUSIONS = {
  // Lowercase L, digit 1, uppercase I, pipe
  'l': '[lI1|]',
  'I': '[Il1|]',
  '1': '[1lI|]',
  '|': '[|lI1]',

  // Uppercase O and digit 0
  'O': '[O0]',
  '0': '[0O]',
  'o': '[oO0]',

  // Colon, semicolon (common OCR swap)
  ':': '[:;]',
  ';': '[;:]',

  // Period, comma (common OCR swap)
  '.': '[.,]',
  ',': '[,.]',

  // Uppercase S and digit 5
  'S': '[S5]',
  '5': '[5S]',

  // Uppercase B and digit 8
  'B': '[B8]',
  '8': '[8B]',

  // Uppercase G and digit 6
  'G': '[G6]',
  '6': '[6G]',

  // Lowercase rn misread as m
  // (handled at bigram level below)

  // Hyphen, en dash, em dash
  '-': '[-–—]',
  '–': '[-–—]',
  '—': '[-–—]',

  // Smart quotes and straight quotes
  "'": "[''`]",
  "'": "[''`]",
  '"': '[""\u201c\u201d]',
};

/**
 * Characters that must be escaped in regex.
 */
const REGEX_SPECIAL = /[.*+?^${}()|[\]\\]/g;

/**
 * Generates a regex pattern from anchor text that tolerates common OCR misreads.
 * 
 * @param anchorText - The original anchor text (e.g., "Invoice Number:")
 * @param options - Configuration options
 * @param options.caseInsensitive - If true, adds (?i) flag notation (default true)
 * @param options.flexibleSpacing - If true, allows variable whitespace between words (default true)
 * @param options.trailingPunctuation - If true, makes trailing punctuation optional (default true)
 * @returns A regex pattern string
 * 
 * @example
 * suggestAnchorPattern("Invoice Number:")
 * // Returns: "[Il1|]nvo[Il1|]ce\\s+Number[:;]?"
 */
export function suggestAnchorPattern(anchorText, options = {}) {
  const {
    flexibleSpacing = true,
    trailingPunctuation = true,
  } = options;

  if (!anchorText || anchorText.trim().length === 0) {
    return '';
  }

  const text = anchorText.trim();
  const chars = [...text];
  let pattern = '';
  let i = 0;

  while (i < chars.length) {
    const char = chars[i];

    // Handle spaces: allow flexible whitespace
    if (char === ' ' || char === '\t') {
      if (flexibleSpacing) {
        // Collapse consecutive spaces into one flexible whitespace token
        while (i + 1 < chars.length && (chars[i + 1] === ' ' || chars[i + 1] === '\t')) {
          i++;
        }
        pattern += '\\s+';
      } else {
        pattern += ' ';
      }
      i++;
      continue;
    }

    // Check for OCR confusion mapping
    if (OCR_CONFUSIONS[char]) {
      pattern += OCR_CONFUSIONS[char];
      i++;
      continue;
    }

    // Default: escape special regex chars, pass through normal chars
    if (REGEX_SPECIAL.test(char)) {
      // If this is the last character and trailingPunctuation is on, make it optional
      if (trailingPunctuation && i === chars.length - 1 && /[.:;,!?]/.test(char)) {
        pattern += char.replace(REGEX_SPECIAL, '\\$&') + '?';
      } else {
        pattern += char.replace(REGEX_SPECIAL, '\\$&');
      }
    } else {
      pattern += char;
    }

    i++;
  }

  return pattern;
}

/**
 * Tests whether a given regex pattern is valid.
 * 
 * @param pattern - The regex pattern string to validate
 * @returns Object with `valid` boolean and optional `error` string
 */
export function validateRegexPattern(pattern) {
  if (!pattern || pattern.trim().length === 0) {
    return { valid: false, error: 'Pattern is empty' };
  }
  try {
    new RegExp(pattern, 'i');
    return { valid: true, error: null };
  } catch (e) {
    return { valid: false, error: e.message };
  }
}

/**
 * Tests a regex pattern against a sample text string.
 * Returns match details for preview in the UI.
 * 
 * @param pattern - The regex pattern string
 * @param text - The text to test against
 * @returns Object with match result
 */
export function testPatternAgainstText(pattern, text) {
  if (!pattern || !text) {
    return { matched: false, matchText: null, index: -1 };
  }
  try {
    const regex = new RegExp(pattern, 'i');
    const match = regex.exec(text);
    if (match) {
      return {
        matched: true,
        matchText: match[0],
        index: match.index,
      };
    }
    return { matched: false, matchText: null, index: -1 };
  } catch {
    return { matched: false, matchText: null, index: -1, error: 'Invalid regex' };
  }
}

/**
 * Generates alternative anchor text strings from common OCR misreads.
 * Unlike suggestAnchorPattern (which produces regex), this produces
 * plain-text alternatives for the anchor_alternatives JSONB column.
 * 
 * @param anchorText - The original anchor text
 * @param maxAlternatives - Maximum number of alternatives to generate (default 5)
 * @returns Array of alternative anchor text strings
 * 
 * @example
 * generateAlternatives("Invoice Number:")
 * // Returns: ["1nvoice Number:", "lnvoice Number:", "Invoice Number;", "Invoice Number"]
 */
export function generateAlternatives(anchorText, maxAlternatives = 5) {
  if (!anchorText || anchorText.trim().length === 0) return [];

  const text = anchorText.trim();
  const alternatives = new Set();

  // Strategy 1: Common first-character confusions (I/l/1)
  const firstChar = text[0];
  const rest = text.slice(1);
  const firstConfusions = {
    'I': ['l', '1'], 'l': ['I', '1'], '1': ['I', 'l'],
    'O': ['0'], '0': ['O'],
    'S': ['5'], '5': ['S'],
    'B': ['8'], '8': ['B'],
  };
  if (firstConfusions[firstChar]) {
    for (const alt of firstConfusions[firstChar]) {
      alternatives.add(alt + rest);
    }
  }

  // Strategy 2: Trailing punctuation swap/removal
  const lastChar = text[text.length - 1];
  const body = text.slice(0, -1);
  if (lastChar === ':') {
    alternatives.add(body + ';');  // colon → semicolon
    alternatives.add(body);         // colon removed
  } else if (lastChar === ';') {
    alternatives.add(body + ':');
    alternatives.add(body);
  } else if (lastChar === '.') {
    alternatives.add(body + ',');
    alternatives.add(body);
  }

  // Strategy 3: Inner l/1/I swaps (only first occurrence)
  for (let i = 1; i < text.length; i++) {
    const c = text[i];
    if (c === 'l') {
      alternatives.add(text.slice(0, i) + '1' + text.slice(i + 1));
      break;
    }
    if (c === '1') {
      alternatives.add(text.slice(0, i) + 'l' + text.slice(i + 1));
      break;
    }
  }

  // Remove original text if it somehow ended up in alternatives
  alternatives.delete(text);

  return [...alternatives].slice(0, maxAlternatives);
}
